import React from "react";
import { X, PlusCircle, AlertTriangle, RefreshCw } from "lucide-react";

const ChartEditPopUp = ({
  newItem,
  setNewItem,
  chartTypes,
  formatChartTypeName,
  savedQueries,
  xAxisOptions,
  yAxisOptions,
  setXAxisOptions,
  setYAxisOptions,
  chartColors,
  setLoader,
  getThemeURL,
  getData,
  handleYAxisKeyChange,
  handleYAxisLabelChange,
  handleRemoveYAxisKey,
  handleAddYAxisKey,
  labelStyle,
  dropdownStyle,
  xAxisOrientationOptions,
  handleDateFieldsUpdate,
  selectedBankIds,
}) => {
  const [noDataForDateRange, setNoDataForDateRange] = React.useState(false);

  // Helper function to determine filter fields dynamically
  const determineFilterFields = (queryObject) => {
    // Extract table name from entity
    const entity = queryObject.entity || "";
    const tableName = entity.includes(".") ? entity.split(".")[1] : entity;

    // Get joins information
    const joins = queryObject.joins || [];
    const joinedTables = joins.map((join) => {
      const table = join.table || join.alias || "";
      return table.includes(".") ? table.split(".")[1] : table;
    });

    // Helper function to determine filter field names
    let bankFilterField = null;

    // Check what tables are available
    const hasBankTable = tableName === "bank" || joinedTables.includes("bank");
    const hasJoins = joins.length > 0;

    console.log("ChartEditPopUp: Table analysis:", {
      tableName,
      joinedTables,
      hasBankTable,
      hasJoins,
    });

    // Determine bank filter field
    if (tableName === "bank") {
      // If entity is bank table
      bankFilterField = "bank.id";
    } else if (hasBankTable && hasJoins) {
      // If joins are there and bank table is available
      bankFilterField = "bank.id";
    } else if (
      !hasBankTable &&
      tableName !== "bank" &&
      tableName !== "branch_details"
    ) {
      // For core tables (not bank or branch_details), use table.bank_id
      bankFilterField = `${tableName}.bank_id`;
    } else if (!hasBankTable) {
      // If no bank table available, use current table with bank_id
      bankFilterField = `${tableName}.bank_id`;
    }

    console.log(
      "ChartEditPopUp: Determined bank filter field:",
      bankFilterField
    );

    return { bankFilterField };
  };

  // Function to identify date/timestamp fields from the data
  const getDateFields = () => {
    if (!newItem.data || newItem.data.length === 0) {
      return [];
    }

    const dateFields = [];
    const sampleRow = newItem.data[0];

    Object.keys(sampleRow).forEach((key) => {
      const value = sampleRow[key];

      // Check if the value looks like a date or timestamp
      if (typeof value === "string") {
        // Check for various date formats
        const datePatterns = [
          /^\d{4}-\d{2}-\d{2}$/, // YYYY-MM-DD (date)
          /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/, // ISO timestamp
          /^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}:\d{2}/, // YYYY-MM-DD HH:MM:SS (timestamp)
        ];

        const isDateField = datePatterns.some((pattern) => pattern.test(value));

        if (isDateField) {
          // Determine the full column name (with table prefix)
          const tableName = newItem.dataSource
            ? savedQueries.find((q) => q.serviceName === newItem.dataSource)
                ?.queryBuilder
            : null;

          let fullColumnName = key;
          if (tableName) {
            try {
              const queryObject =
                typeof tableName === "string"
                  ? JSON.parse(tableName)
                  : tableName;
              const tablePrefix = queryObject.entity
                ? queryObject.entity.split(".")[1]
                : "";
              if (tablePrefix) {
                fullColumnName = `${tablePrefix}.${key}`;
              }
            } catch (error) {
              console.warn("Could not parse table name:", error);
            }
          }

          // Determine field type based on format
          let fieldType = "date";
          if (value.includes("T") || value.includes(" ")) {
            fieldType = "timestamp";
          }

          dateFields.push({
            key: key,
            fullName: fullColumnName,
            label: key
              .replace(/([A-Z])/g, " $1")
              .replace(/^./, (str) => str.toUpperCase()),
            value: value,
            type: fieldType,
          });
        }
      }
    });

    return dateFields;
  };

  // Helper function to handle refresh button click with dynamic date field
  const handleRefreshData = async () => {
    if (
      !newItem.dataSource ||
      !newItem.fromDate ||
      !newItem.toDate ||
      !newItem.selectedDateField
    ) {
      return;
    }

    setLoader(true);
    const matchedQuery = savedQueries.find(
      (q) => q.serviceName === newItem.dataSource
    );

    if (!matchedQuery) {
      setLoader(false);
      return;
    }

    const queryObject =
      typeof matchedQuery.queryBuilder === "string"
        ? JSON.parse(matchedQuery.queryBuilder)
        : matchedQuery.queryBuilder;

    // Helper function to check if a string is a valid date
    const isValidDate = (dateString) => {
      if (typeof dateString !== "string") return false;

      const datePatterns = [
        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}[+-]\d{4}$/,
        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/,
        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/,
        /^\d{4}-\d{2}-\d{2}$/,
      ];
      return (
        datePatterns.some((pattern) => pattern.test(dateString)) &&
        !isNaN(Date.parse(dateString))
      );
    };

    // Helper function to format date to dd-mm-yyyy
    const formatDate = (dateString) => {
      const date = new Date(dateString);
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${year}-${month}-${day}`;
    };

    const filters = {};

    try {
      const userResponse = await getData(getThemeURL);
      if (
        userResponse &&
        Array.isArray(userResponse) &&
        userResponse.length > 0
      ) {
        const user = userResponse[0];

        // **NEW**: Apply dynamic filter logic for date filter refresh
        if (user.role_name === "SUPER ADMIN") {
          // For Super Admin, use selectedBankIds if available
          if (selectedBankIds && selectedBankIds.length > 0) {
            // **NEW**: Use dynamic filter field determination
            const { bankFilterField } = determineFilterFields(queryObject);

            if (bankFilterField) {
              filters[bankFilterField] = {
                operator: "IN",
                value: selectedBankIds.map(Number),
              };
              console.log(
                "Applied dynamic bank filter for Super Admin in date filter:",
                filters[bankFilterField]
              );
            }
          }
        } else {
          // For non-Super Admin users, use their specific IDs with dynamic field determination
          const { bankFilterField } = determineFilterFields(queryObject);

          if (user.bank_id && bankFilterField) {
            filters[bankFilterField] = {
              operator: "IN",
              value: [user.bank_id], // Convert single ID to array format
            };
            console.log(
              "Applied dynamic bank filter for regular user in date filter:",
              filters[bankFilterField]
            );
          }
        }
      }
    } catch (error) {
      console.error("Error fetching user profile for filters:", error);
    }

    // Create date filter with timestamp handling
    const dateFilter = {};

    // Check if the selected field is a timestamp type
    const selectedFieldData = dateFields.find(
      (field) => field.fullName === newItem.selectedDateField
    );
    const isTimestamp =
      selectedFieldData && selectedFieldData.type === "timestamp";
    handleDateFieldsUpdate(dateFields);

    if (isTimestamp) {
      // Append timestamp to fromDate and toDate for timestamp fields with space
      const fromDateTime = newItem.fromDate + " 00:00:00.000";
      const toDateTime = newItem.toDate + " 23:59:59.999"; // Use end of day for toDate

      dateFilter[newItem.selectedDateField] = {
        operator: "BETWEEN",
        value: [fromDateTime, toDateTime],
      };
    } else {
      // Use date values as-is for date-only fields
      dateFilter[newItem.selectedDateField] = {
        operator: "BETWEEN",
        value: [newItem.fromDate, newItem.toDate],
      };
    }

    const enhancedQuery = {
      ...queryObject,
      filters: {
        ...queryObject.filters,
        ...filters,
        ...dateFilter,
      },
    };

    console.log(
      "Query sent to /nifi/fetchQuery with dynamic date filter:",
      enhancedQuery
    );

    try {
      const response = await fetch("/nifi/fetchQuery", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(enhancedQuery),
      });

      const result = await response.json();
      console.log("Executed Query Result with date filters:", result);

      if (result.data.length === 0) {
        setNoDataForDateRange(true);
        return;
      } else {
        setNoDataForDateRange(false);
      }
      // Transform the data to format dates
      const transformedData = result.data.map((item) => {
        const transformedItem = { ...item };

        Object.keys(transformedItem).forEach((key) => {
          if (isValidDate(transformedItem[key])) {
            transformedItem[key] = formatDate(transformedItem[key]);
          }
        });

        return transformedItem;
      });

      // Extract keys from the first data item to determine data types
      if (result.data && result.data.length > 0) {
        const firstDataItem = result.data[0];
        const xAxisOptions = [];
        const yAxisOptions = [];

        // Use the first transformed item to determine axis options
        const firstTransformedItem = transformedData[0];

        // Iterate through the keys of the first transformed data item
        Object.keys(firstTransformedItem).forEach((key) => {
          const value = firstTransformedItem[key];

          // Check if value is a string (for xAxis) - this now includes formatted dates
          if (typeof value === "string") {
            xAxisOptions.push(key);
          }
          // Check if value is a number (for yAxis)
          else if (typeof value === "number") {
            yAxisOptions.push(key);
          }
        });

        // Create dynamic sourceConfig
        const dynamicSourceConfig = {
          data: transformedData,
          xAxisOptions: xAxisOptions,
          yAxisOptions: yAxisOptions,
          defaultChartType: "line",
        };

        console.log("Refreshed data for charts", dynamicSourceConfig);

        setXAxisOptions(dynamicSourceConfig.xAxisOptions);
        setYAxisOptions(dynamicSourceConfig.yAxisOptions);
        setNewItem({
          ...newItem,
          data: transformedData,
          dateFilterApplied: true,
        });
      }
    } catch (error) {
      console.error("Error fetching query data:", error);
    } finally {
      setLoader(false);
    }
  };

  // Helper function to get pie color for a specific index
  const getPieColor = (index) => {
    const pieColorEntry = newItem.pieColors?.find((pc) => pc.index === index);
    return pieColorEntry
      ? pieColorEntry.color
      : chartColors[index % chartColors.length];
  };

  // Helper function to update pie color for a specific index
  const updatePieColor = (index, color) => {
    const existingPieColors = newItem.pieColors || [];
    const existingEntryIndex = existingPieColors.findIndex(
      (pc) => pc.index === index
    );

    let updatedPieColors;
    if (existingEntryIndex !== -1) {
      // Update existing entry
      updatedPieColors = existingPieColors.map((pc, i) =>
        i === existingEntryIndex ? { ...pc, color } : pc
      );
    } else {
      // Add new entry
      updatedPieColors = [...existingPieColors, { index, color }];
    }

    console.log("Updated Pie Colors:", updatedPieColors);

    setNewItem({
      ...newItem,
      pieColors: updatedPieColors,
    });
  };

  // Get available date fields
  const dateFields = getDateFields();

  return (
    <>
      {/* Chart Type and Data Source Selection */}
      <div
        style={{
          display: "flex",
          gap: "8px",
          justifyContent: "space-between",
        }}
      >
        {/* Chart Type Selection */}
        <div style={{ minWidth: "385px" }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontSize: "14px",
              fontWeight: "bold",
              color: "#374151",
            }}
          >
            Chart Type
          </label>
          <select
            value={newItem.chartType}
            onChange={(e) => {
              const selectedType = e.target.value;
              // Reset chart type and clear all related fields when chart type changes
              setNewItem({
                ...newItem,
                chartType: selectedType,
                dataSource: "", // Clear data source
                data: [], // Clear data
                fromDate: "",
                toDate: "",
                selectedDateField: "", // Clear selected date field
                xAxisKey: "", // Clear X-axis key
                yAxisKeys: [], // Clear Y-axis keys
                yAxisLabels: [], // Clear Y-axis labels
                xAxisLabelFontSize: "",
                yAxisLabelFontSize: "",
                legendFontSize: "",
                colors: [], // Clear colors
                // Reset any other chart-specific properties
                curveType: "monotone",
                legendPosition: "bottom",
                animation: "default",
                gridLines: true,
                // Reset chart type specific properties
                ...(selectedType === "bar" && {
                  layout: "horizontal",
                  barWidth: 30,
                }),
                ...(selectedType === "pie" && {
                  innerRadius: 0,
                  showLabels: true,
                }),
                ...(selectedType === "area" && {
                  areaOpacity: 50,
                  showGradient: false,
                }),
              });

              // Also clear the axis options in the parent component
              setXAxisOptions([]);
              setYAxisOptions([]);
            }}
            style={{
              width: "93%",
              padding: "10px 12px",
              borderRadius: "8px",
              border: "1px solid #d1d5db",
              backgroundColor: "white",
              fontSize: "14px",
              color: "#374151",
              outline: "none",
              transition: "border-color 0.2s",
            }}
          >
            {chartTypes
              .slice() // create a copy so original isn't mutated
              .sort((a, b) => a.localeCompare(b)) // alphabetical sort
              .map((type) => (
                <option key={type} value={type}>
                  {formatChartTypeName(type)} Chart
                </option>
              ))}
          </select>
        </div>

        {/* Data Source Selection */}
        <div style={{ flex: 1 }}>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontSize: "14px",
              fontWeight: "bold",
              color: "#374151",
              marginLeft: "-23px",
            }}
          >
            Data Source<span style={{ color: "red" }}>*</span>
          </label>
          <select
            value={newItem.dataSource || ""}
            onChange={async (e) => {
              const serviceName = e.target.value;
              setLoader(true);
              const matchedQuery = savedQueries.find(
                (q) => q.serviceName === serviceName
              );

              if (!matchedQuery) {
                setLoader(false);
                return;
              }

              const queryObject =
                typeof matchedQuery.queryBuilder === "string"
                  ? JSON.parse(matchedQuery.queryBuilder)
                  : matchedQuery.queryBuilder;

              // Helper functions remain the same...
              const isValidDate = (dateString) => {
                if (typeof dateString !== "string") return false;

                const datePatterns = [
                  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}[+-]\d{4}$/,
                  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}Z$/,
                  /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/,
                  /^\d{4}-\d{2}-\d{2}$/,
                ];

                return (
                  datePatterns.some((pattern) => pattern.test(dateString)) &&
                  !isNaN(Date.parse(dateString))
                );
              };

              const formatDate = (dateString) => {
                const date = new Date(dateString);
                const day = String(date.getDate()).padStart(2, "0");
                const month = String(date.getMonth() + 1).padStart(2, "0");
                const year = date.getFullYear();
                return `${year}-${month}-${day}`;
              };

              const filters = {};

              try {
                const userResponse = await getData(getThemeURL);
                if (
                  userResponse &&
                  Array.isArray(userResponse) &&
                  userResponse.length > 0
                ) {
                  const user = userResponse[0];

                  // **NEW**: Apply dynamic filter logic for data source selection
                  if (user.role_name === "SUPER ADMIN") {
                    // For Super Admin, use selectedBankIds if available
                    if (selectedBankIds && selectedBankIds.length > 0) {
                      // **NEW**: Use dynamic filter field determination
                      const { bankFilterField } =
                        determineFilterFields(queryObject);

                      if (bankFilterField) {
                        filters[bankFilterField] = {
                          operator: "IN",
                          value: selectedBankIds.map(Number),
                        };
                        console.log(
                          "Applied dynamic bank filter for Super Admin:",
                          filters[bankFilterField]
                        );
                      }
                    }
                  } else {
                    // For non-Super Admin users, use their specific IDs with dynamic field determination
                    const { bankFilterField } =
                      determineFilterFields(queryObject);

                    if (user.bank_id && bankFilterField) {
                      filters[bankFilterField] = {
                        operator: "IN",
                        value: [user.bank_id], // Convert single ID to array format
                      };
                      console.log(
                        "Applied dynamic bank filter for regular user:",
                        filters[bankFilterField]
                      );
                    }
                  }
                }
              } catch (error) {
                console.error(
                  "Error fetching user profile for filters:",
                  error
                );
              }

              const enhancedQuery = {
                ...queryObject,
                filters: {
                  ...queryObject.filters,
                  ...filters,
                },
              };
              console.log("Query sent to /nifi/fetchQuery:", enhancedQuery);

              try {
                const response = await fetch("/nifi/fetchQuery", {
                  method: "POST",
                  headers: {
                    "Content-Type": "application/json",
                  },
                  body: JSON.stringify(enhancedQuery),
                });

                const result = await response.json();
                console.log("Executed Query Result of charts:", result);

                // Transform the data to format dates
                const transformedData = result.data.map((item) => {
                  const transformedItem = { ...item };

                  Object.keys(transformedItem).forEach((key) => {
                    if (isValidDate(transformedItem[key])) {
                      transformedItem[key] = formatDate(transformedItem[key]);
                    }
                  });

                  return transformedItem;
                });

                // Extract keys from the first data item to determine data types
                if (result.data && result.data.length > 0) {
                  const xAxisOptions = [];
                  const yAxisOptions = [];

                  // Use the first transformed item to determine axis options
                  const firstTransformedItem = transformedData[0];

                  Object.keys(firstTransformedItem).forEach((key) => {
                    const value = firstTransformedItem[key];

                    if (typeof value === "string") {
                      xAxisOptions.push(key);
                    } else if (typeof value === "number") {
                      yAxisOptions.push(key);
                    }
                  });

                  const dynamicSourceConfig = {
                    data: transformedData,
                    xAxisOptions: xAxisOptions,
                    yAxisOptions: yAxisOptions,
                    defaultChartType: "line",
                  };

                  console.log("Data for charts", dynamicSourceConfig);

                  setXAxisOptions(dynamicSourceConfig.xAxisOptions);
                  setYAxisOptions(dynamicSourceConfig.yAxisOptions);
                  setNewItem({
                    ...newItem,
                    dataSource: serviceName,
                    data: transformedData,
                    fromDate: "",
                    toDate: "",
                    selectedDateField: "", // Reset date field selection when data source changes
                    dateFilterApplied: false,
                    dateFieldsChanged: false,
                    xAxisKey: dynamicSourceConfig.xAxisOptions[0] || "",
                    yAxisKeys: dynamicSourceConfig.yAxisOptions.slice(0, 1) || [
                      "",
                    ],
                    colors:
                      dynamicSourceConfig.yAxisOptions
                        .slice(0, 1)
                        .map((_, i) => chartColors[i % chartColors.length]) ||
                      [],
                    chartType:
                      newItem.chartType ||
                      dynamicSourceConfig.defaultChartType ||
                      "line",
                    pieColors: [],
                  });
                }
              } catch (error) {
                console.error("Error fetching query data:", error);
              } finally {
                setLoader(false);
              }
            }}
            style={{
              width: "106%",
              padding: "10px 12px",
              borderRadius: "8px",
              border: "1px solid #d1d5db",
              backgroundColor: "white",
              fontSize: "14px",
              color: "#374151",
              outline: "none",
              marginLeft: "-26px",
            }}
          >
            <option value="">Select Data Source</option>
            {savedQueries.map((query) => (
              <option key={query.id} value={query.serviceName}>
                {query.serviceName}
              </option>
            ))}
          </select>
          {!newItem.dataSource && (
            <span
              style={{
                color: "#ef4444",
                fontSize: "12px",
                marginTop: "4px",
                display: "block",
              }}
            >
              Data source is required
            </span>
          )}
        </div>
      </div>

      {/* Common axis configuration */}

      {/* Container for X-Axis and Y-Axis inputs on the same row */}
      {newItem.chartType !== "radialProgress" &&
        newItem.chartType !== "ranker" && (
          <div
            style={{
              display: "flex",
              gap: "16px",
              alignItems: "flex-start",
            }}
          >
            {/* X-Axis Key Section */}
            {newItem.chartType !== "pie" && (
              <div style={{ minWidth: "285px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: "#374151",
                  }}
                >
                  X-Axis Key
                </label>
                <select
                  value={newItem.xAxisKey}
                  onChange={(e) =>
                    setNewItem({
                      ...newItem,
                      xAxisKey: e.target.value,
                    })
                  }
                  disabled={!newItem.dataSource}
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: "1px solid #d1d5db",
                    backgroundColor: "white",
                    fontSize: "14px",
                    color: "#374151",
                    outline: "none",
                  }}
                >
                  <option value="">Select X-Axis</option>
                  {xAxisOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {/* Y-Axis Keys Section */}

            {newItem.chartType === "pie" ? (
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: "#374151",
                  }}
                >
                  Field
                </label>
                <select
                  value={newItem.yAxisKeys[0] || ""}
                  onChange={(e) => {
                    const updatedItem = { ...newItem };
                    updatedItem.yAxisKeys = [e.target.value];
                    setNewItem(updatedItem);
                  }}
                  style={{
                    flex: 1,
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: "1px solid #d1d5db",
                    backgroundColor: "white",
                    fontSize: "14px",
                    color: "#374151",
                    outline: "none",
                    minWidth: "385px",
                  }}
                >
                  <option value="">Select Field</option>
                  {yAxisOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: "#374151",
                  }}
                >
                  {newItem.chartType === "pie" ? "Field" : "Y-Axis Keys"}
                </label>
                {newItem.yAxisKeys.map((key, index) => {
                  // For pie charts, only show the first Y-axis key
                  if (newItem.chartType === "pie" && index > 0) {
                    return null;
                  }
                  return (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        gap: "8px",
                        marginBottom: "8px",
                        width: newItem.chartType === "pie" ? "385px" : "100%",
                      }}
                    >
                      <select
                        value={key}
                        onChange={(e) =>
                          handleYAxisKeyChange(index, e.target.value)
                        }
                        disabled={!newItem.dataSource}
                        style={{
                          flex: 1,
                          padding: "10px 12px",
                          borderRadius: "8px",
                          border: "1px solid #d1d5db",
                          backgroundColor: "white",
                          fontSize: "14px",
                          color: "#374151",
                          outline: "none",
                        }}
                      >
                        <option value="">Select Y-Axis</option>
                        {yAxisOptions.map((option) => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>

                      {/* Hide custom label input for pie charts */}
                      {newItem.chartType !== "pie" &&
                        newItem.chartType !== "radialProgress" &&
                        newItem.chartType !== "ranker" && (
                          <input
                            type="text"
                            placeholder="Enter Label"
                            value={newItem.yAxisLabels?.[index] || ""}
                            onChange={(e) =>
                              handleYAxisLabelChange(index, e.target.value)
                            }
                            style={{
                              flex: 1,
                              padding: "10px 12px",
                              borderRadius: "8px",
                              border: "1px solid #d1d5db",
                              backgroundColor: "white",
                              fontSize: "14px",
                              color: "#374151",
                              outline: "none",
                            }}
                          />
                        )}
                      {/* Hide remove button for pie charts since there's only one Y-axis */}
                      {newItem.chartType !== "pie" &&
                        newItem.chartType !== "radialProgress" &&
                        newItem.chartType !== "ranker" && (
                          <button
                            type="button"
                            onClick={() => handleRemoveYAxisKey(index)}
                            style={{
                              padding: "0 12px",
                              backgroundColor: "#fef2f2",
                              border: "none",
                              borderRadius: "8px",
                              color: "#dc2626",
                              cursor: "pointer",
                            }}
                          >
                            <X size={16} />
                          </button>
                        )}
                    </div>
                  );
                })}
                {/* Hide "Add Y-Axis Key" button for pie charts */}
                {newItem.chartType !== "pie" &&
                  newItem.chartType !== "radialProgress" &&
                  newItem.chartType !== "ranker" && (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                      }}
                    >
                      <button
                        type="button"
                        onClick={handleAddYAxisKey}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          backgroundColor: "#f0fdf4",
                          color: "#16a34a",
                          padding: "8px 12px",
                          borderRadius: "8px",
                          border: "none",
                          fontSize: "14px",
                          fontWeight: "500",
                        }}
                      >
                        <PlusCircle size={16} />
                        Add Y-Axis Key
                      </button>
                    </div>
                  )}
              </div>
            )}
          </div>
        )}

      {[
        "line",
        "area",
        "bar",
        "composed",
        "stackedBar",
        "trend",
        "radar",
      ].includes(newItem.chartType) && (
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div>
            <label style={labelStyle}>X-Axis Label Font Size</label>
            <input
              type="number"
              min={8}
              max={72}
              value={newItem.xAxisLabelFontSize || 14}
              onChange={(e) => {
                const fontSize = parseInt(e.target.value, 10);
                if (!isNaN(fontSize) && fontSize >= 8 && fontSize <= 72) {
                  console.log("xAxisLabelFontSize set to:", fontSize);
                  setNewItem({ ...newItem, xAxisLabelFontSize: fontSize });
                }
              }}
              style={{
                width: "60px",
                padding: "4px 6px",
                borderRadius: "4px",
                border: "1px solid #d1d5db",
                fontSize: "14px",
              }}
            />
          </div>
          <div>
            <label style={labelStyle}>Y-Axis Label Font Size</label>
            <input
              type="number"
              min={8}
              max={72}
              value={newItem.yAxisLabelFontSize || 14}
              onChange={(e) => {
                const fontSize = parseInt(e.target.value, 10);
                if (!isNaN(fontSize) && fontSize >= 8 && fontSize <= 72) {
                  setNewItem({ ...newItem, yAxisLabelFontSize: fontSize });
                }
              }}
              style={{
                width: "60px",
                padding: "4px 6px",
                borderRadius: "4px",
                border: "1px solid #d1d5db",
                fontSize: "14px",
              }}
            />
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: "16px" }}>
        <div>
          <label
            style={{
              display: "block",
              marginBottom: "8px",
              fontSize: "14px",
              fontWeight: "bold",
              color: "#374151",
            }}
          >
            Legend Font Size
          </label>
          <input
            type="number"
            min={8}
            max={72}
            value={newItem.legendFontSize || 14}
            onChange={(e) => {
              const fontSize = parseInt(e.target.value, 10);
              if (!isNaN(fontSize) && fontSize >= 8 && fontSize <= 72) {
                setNewItem({ ...newItem, legendFontSize: fontSize });
              }
            }}
            style={{
              width: "80px",
              padding: "8px 10px",
              borderRadius: "6px",
              border: "1px solid #d1d5db",
              fontSize: "14px",
            }}
          />
        </div>
      </div>

      {/* Colors Section */}
      <div
        style={{
          display: "flex",
          gap: "8px",
          marginBottom: "16px",
        }}
      >
        <div style={{ minWidth: "385px" }}>
          {!["pie"].includes(newItem.chartType) &&
            newItem.chartType !== "radialProgress" &&
            newItem.chartType !== "ranker" && (
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: "#374151",
                  }}
                >
                  Colors
                </label>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                  }}
                >
                  {newItem.yAxisKeys.map((key, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <input
                        type="color"
                        value={
                          newItem.colors[index] ||
                          chartColors[index % chartColors.length]
                        }
                        onChange={(e) => {
                          const updatedColors = [...newItem.colors];
                          updatedColors[index] = e.target.value;
                          setNewItem({
                            ...newItem,
                            colors: updatedColors,
                          });
                        }}
                        style={{
                          width: "30px",
                          height: "30px",
                          border: "1px solid #d1d5db",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                      />
                      <span style={{ fontSize: "12px" }}>
                        {key || `Series ${index + 1}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

          {newItem.chartType === "pie" && (
            <>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: "#374151",
                  }}
                >
                  Colors
                </label>
                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "8px",
                  }}
                >
                  {newItem.data.map((entry, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <input
                        type="color"
                        value={getPieColor(index)}
                        onChange={(e) => updatePieColor(index, e.target.value)}
                        style={{
                          width: "30px",
                          height: "30px",
                          border: "1px solid #d1d5db",
                          borderRadius: "4px",
                          cursor: "pointer",
                        }}
                      />
                      <span style={{ fontSize: "12px" }}>
                        {entry[newItem.xAxisKey] || `Slice ${index + 1}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
        {/* Empty flex space for proper alignment */}
        <div style={{ flex: 1 }}>
          {/* Any other content that should be beside Colors can go here */}
        </div>
      </div>

      {/* Date Filter Section - UPDATED WITH CONSISTENT LAYOUT */}
      <div style={{ marginBottom: "24px" }}>
        <label
          style={{
            display: "block",
            marginBottom: "16px",
            fontSize: "16px",
            fontWeight: "bold",
            color: "#374151",
          }}
        >
          Data filtering based on date range
        </label>

        <div
          style={{
            display: "flex",
            gap: "16px",
            alignItems: "flex-start", // Changed from "flex-end" to "flex-start"
            flexWrap: "wrap",
          }}
        >
          {/* Date Field Selector Dropdown */}
          <div style={{ flex: 1, minWidth: "180px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "bold",
                color: "#374151",
              }}
            >
              Date Field
            </label>
            <select
              value={newItem.selectedDateField || ""}
              onChange={(e) => {
                setNewItem({
                  ...newItem,
                  selectedDateField: e.target.value,
                  dateFilterApplied: false,
                  dateFieldsChanged: true,
                  fromDate: "", // Clear fromDate when date field changes
                  toDate: "", // Clear toDate when date field changes
                  chartDateValidationError: null,
                  toDateValidationError: null,
                });
                setNoDataForDateRange(false);
              }}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                fontSize: "14px",
                height: "40px",
                boxSizing: "border-box",
                backgroundColor: "white",
              }}
            >
              <option value="">Select date field</option>
              {dateFields.map((field) => (
                <option key={field.fullName} value={field.fullName}>
                  {field.label}
                </option>
              ))}
            </select>

            {/* Reserved space for consistency */}
            <div
              style={{
                height: "32px", // Fixed height to prevent layout shifts
                display: "flex",
                alignItems: "flex-start",
                paddingTop: "4px",
              }}
            >
              {/* Empty space for consistency */}
            </div>
          </div>

          {/* From Date */}
          <div style={{ flex: 1, minWidth: "150px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "bold",
                color: "#374151",
              }}
            >
              From Date
            </label>
            <input
              type="date"
              value={newItem.fromDate || ""}
              disabled={!newItem.selectedDateField}
              onChange={(e) => {
                const selectedDate = e.target.value;
                const today = new Date().toISOString().split("T")[0];

                if (selectedDate > today) {
                  const updatedItem = {
                    ...newItem,
                    fromDate: today,
                    dateFieldsChanged: true,
                    chartDateValidationError:
                      "Future dates are not allowed. Date has been set to today.",
                  };

                  if (newItem.toDate && today > newItem.toDate) {
                    updatedItem.toDate = "";
                    updatedItem.toDateValidationError =
                      "To Date cleared because From Date was adjusted to today.";
                  }

                  setNewItem(updatedItem);

                  setTimeout(() => {
                    setNewItem((prev) => ({
                      ...prev,
                      chartDateValidationError: null,
                    }));
                  }, 3000);

                  return;
                }

                const updatedItem = {
                  ...newItem,
                  fromDate: selectedDate,
                  chartDateValidationError: null,
                  dateFilterApplied: false,
                  dateFieldsChanged: true,
                };

                if (newItem.toDate && newItem.toDate < selectedDate) {
                  updatedItem.toDate = "";
                  updatedItem.toDateValidationError =
                    "To Date cleared because it was earlier than From Date.";
                }

                setNewItem(updatedItem);
                setNoDataForDateRange(false);
              }}
              max={new Date().toISOString().split("T")[0]}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "6px",
                border: newItem.chartDateValidationError
                  ? "1px solid #ef4444"
                  : "1px solid #d1d5db",
                fontSize: "14px",
                height: "40px",
                boxSizing: "border-box",
                backgroundColor: !newItem.selectedDateField
                  ? "#f3f4f6"
                  : "white", // Add visual indication when disabled
                cursor: !newItem.selectedDateField ? "not-allowed" : "pointer",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#1a7a73")}
              onBlur={(e) =>
                (e.target.style.borderColor = newItem.chartDateValidationError
                  ? "#ef4444"
                  : "#d1d5db")
              }
            />

            {/* Reserved space for error message */}
            <div
              style={{
                height: "32px",
                display: "flex",
                alignItems: "flex-start",
                paddingTop: "4px",
              }}
            >
              {newItem.chartDateValidationError && (
                <span
                  style={{
                    color: "#ef4444",
                    fontSize: "12px",
                    lineHeight: "16px",
                  }}
                >
                  {newItem.chartDateValidationError}
                </span>
              )}
            </div>
          </div>

          {/* To Date */}
          <div style={{ flex: 1, minWidth: "150px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "bold",
                color: "#374151",
              }}
            >
              To Date
            </label>
            <input
              type="date"
              value={newItem.toDate || ""}
              disabled={!newItem.selectedDateField}
              onChange={(e) => {
                const selectedDate = e.target.value;
                const today = new Date().toISOString().split("T")[0];

                if (selectedDate > today) {
                  setNewItem({
                    ...newItem,
                    toDate: today,
                    dateFieldsChanged: true,
                    toDateValidationError:
                      "Future dates are not allowed. Date has been set to today.",
                  });

                  setTimeout(() => {
                    setNewItem((prev) => ({
                      ...prev,
                      toDateValidationError: null,
                    }));
                  }, 3000);

                  return;
                }

                if (newItem.fromDate && selectedDate < newItem.fromDate) {
                  setNewItem({
                    ...newItem,
                    toDate: selectedDate,
                    dateFieldsChanged: true,
                    toDateValidationError:
                      "To Date cannot be earlier than From Date.",
                  });

                  return;
                }

                setNewItem({
                  ...newItem,
                  toDate: selectedDate,
                  dateFieldsChanged: true,
                  dateFilterApplied: false,
                  toDateValidationError: null,
                  chartDateValidationError: null,
                });
                setNoDataForDateRange(false);
              }}
              max={new Date().toISOString().split("T")[0]}
              // Remove min as discussed previously
              // min={newItem.fromDate || ""}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "6px",
                border: newItem.toDateValidationError
                  ? "1px solid #ef4444"
                  : "1px solid #d1d5db",
                fontSize: "14px",
                height: "40px",
                boxSizing: "border-box",
                backgroundColor: !newItem.selectedDateField
                  ? "#f3f4f6"
                  : "white", // Add visual indication when disabled
                cursor: !newItem.selectedDateField ? "not-allowed" : "pointer",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#1a7a73")}
              onBlur={(e) =>
                (e.target.style.borderColor = newItem.toDateValidationError
                  ? "#ef4444"
                  : "#d1d5db")
              }
            />

            {/* Reserved space for error message */}
            <div
              style={{
                height: "32px",
                display: "flex",
                alignItems: "flex-start",
                paddingTop: "4px",
              }}
            >
              {newItem.toDateValidationError && (
                <span
                  style={{
                    color: "#ef4444",
                    fontSize: "12px",
                    lineHeight: "16px",
                  }}
                >
                  {newItem.toDateValidationError}
                </span>
              )}
            </div>
          </div>

          {/* Update Filter Button - Positioned to align with input fields */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
            }}
          >
            {/* Invisible label for alignment */}
            <div style={{ height: "24px", marginBottom: "8px" }}></div>

            <button
              onClick={handleRefreshData}
              disabled={
                !newItem.dataSource ||
                !newItem.fromDate ||
                !newItem.toDate ||
                !newItem.selectedDateField ||
                newItem.chartDateValidationError || // Add validation error check
                newItem.toDateValidationError // Add validation error check
              }
              style={{
                padding: "12px 20px",
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                backgroundColor:
                  !newItem.dataSource ||
                  !newItem.fromDate ||
                  !newItem.toDate ||
                  !newItem.selectedDateField ||
                  newItem.chartDateValidationError || // Add validation error check
                  newItem.toDateValidationError // Add validation error check
                    ? "#f3f4f6"
                    : "#1a7a73",
                color:
                  !newItem.dataSource ||
                  !newItem.fromDate ||
                  !newItem.toDate ||
                  !newItem.selectedDateField ||
                  newItem.chartDateValidationError || // Add validation error check
                  newItem.toDateValidationError // Add validation error check
                    ? "#9ca3af"
                    : "white",
                cursor:
                  !newItem.dataSource ||
                  !newItem.fromDate ||
                  !newItem.toDate ||
                  !newItem.selectedDateField ||
                  newItem.chartDateValidationError || // Add validation error check
                  newItem.toDateValidationError // Add validation error check
                    ? "not-allowed"
                    : "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "14px",
                fontWeight: "500",
                height: "40px",
                transition: "all 0.2s ease",
                whiteSpace: "nowrap",
              }}
              title={
                newItem.chartDateValidationError ||
                newItem.toDateValidationError
                  ? "Please fix date validation errors before applying filter"
                  : "Apply date filter and refresh data"
              }
            >
              Update Filter
            </button>

            {/* Reserved space to match other fields */}
            <div style={{ height: "32px" }}></div>
          </div>
        </div>

        {/* Warning Message */}
        {newItem.fromDate &&
          newItem.toDate &&
          newItem.selectedDateField &&
          !newItem.dateFilterApplied &&
          newItem.dateFieldsChanged &&
          !newItem.chartDateValidationError && // Only show when no validation errors
          !newItem.toDateValidationError &&
          !noDataForDateRange && (
            <div
              style={{
                marginTop: "12px",
                padding: "12px 16px",
                backgroundColor: "#fef3c7",
                border: "1px solid #f59e0b",
                borderRadius: "6px",
                color: "#92400e",
                fontSize: "14px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              <AlertTriangle size={16} />
              Click "Update Filter" to apply the date range before saving the
              widget.
            </div>
          )}

        {/* Date Range Validation Error */}
        {newItem.fromDate && !newItem.toDate && (
          <div
            style={{
              marginTop: "12px",
              padding: "12px 16px",
              backgroundColor: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: "6px",
              color: "#dc2626",
              fontSize: "14px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <AlertTriangle size={16} />
            Please select both From Date and To Date to create a valid date
            range for the chart.
          </div>
        )}

        {/* Date Field Selection Validation Error */}
        {newItem.fromDate && newItem.toDate && !newItem.selectedDateField && (
          <div
            style={{
              marginTop: "12px",
              padding: "12px 16px",
              backgroundColor: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: "6px",
              color: "#dc2626",
              fontSize: "14px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <AlertTriangle size={16} />
            Please select a date field to filter by.
          </div>
        )}
      </div>
      {noDataForDateRange && (
        <div
          style={{
            marginTop: "12px",
            padding: "12px 16px",
            backgroundColor: "#f0f9ff",
            border: "1px solid #1a7a73",
            borderRadius: "6px",
            color: "#0f4a45",
            fontSize: "14px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <AlertTriangle size={16} />
          No data found for the selected date range.
        </div>
      )}

      {/* Dynamic fields based on chart type */}
      <div
        style={{
          display: "flex",
          gap: "8px",
          flexDirection:
            newItem.chartType === "scatter" ||
            newItem.chartType === "radar" ||
            newItem.chartType === "funnel" ||
            newItem.chartType === "composed" ||
            newItem.chartType === "stackedBar"
              ? "row-reverse"
              : "row",
        }}
      >
        <div style={{ minWidth: "385px" }}>
          {newItem.chartType === "line" && (
            <>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: "#374151",
                  }}
                >
                  Curve Type
                </label>
                <select
                  value={newItem.curveType || "monotone"}
                  onChange={(e) =>
                    setNewItem({
                      ...newItem,
                      curveType: e.target.value,
                    })
                  }
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: "1px solid #d1d5db",
                    backgroundColor: "white",
                    fontSize: "14px",
                    color: "#374151",
                    outline: "none",
                  }}
                >
                  <option value="monotone">Smooth (Monotone)</option>
                  <option value="linear">Straight (Linear)</option>
                  <option value="step">Step</option>
                </select>
              </div>
            </>
          )}
          {newItem.chartType === "bar" && (
            <>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: "#374151",
                  }}
                >
                  Bar Layout
                </label>
                <select
                  value={newItem.layout || "horizontal"}
                  onChange={(e) =>
                    setNewItem({
                      ...newItem,
                      layout: e.target.value,
                    })
                  }
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: "1px solid #d1d5db",
                    backgroundColor: "white",
                    fontSize: "14px",
                    color: "#374151",
                    outline: "none",
                  }}
                >
                  <option value="horizontal">Horizontal</option>
                </select>
              </div>
              <div style={{ marginTop: "16px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: "#374151",
                  }}
                >
                  Bar Width
                </label>
                <input
                  type="range"
                  min="10"
                  max="100"
                  value={newItem.barWidth || 30}
                  onChange={(e) =>
                    setNewItem({
                      ...newItem,
                      barWidth: parseInt(e.target.value),
                    })
                  }
                />
                <span style={{ marginLeft: "8px" }}>
                  {newItem.barWidth || 30}px
                </span>
              </div>
            </>
          )}
          {newItem.chartType === "radialProgress" &&
            newItem.data &&
            newItem.data.length > 0 && (
              <div style={{ marginBottom: "16px" }}>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: "#374151",
                  }}
                >
                  Customize Progress Bar Colors
                </label>
                <div
                  style={{
                    maxHeight: "200px",
                    overflowY: "auto",
                    border: "1px solid #e5e7eb",
                    borderRadius: "6px",
                    padding: "8px",
                  }}
                >
                  {newItem.data.map((item, index) => {
                    const currentColor =
                      item.color || chartColors[index % chartColors.length];

                    return (
                      <div
                        key={index}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          marginBottom: "8px",
                          padding: "6px",
                          backgroundColor:
                            index % 2 === 0 ? "#f9fafb" : "transparent",
                          borderRadius: "4px",
                        }}
                      >
                        <span
                          style={{
                            flex: 1,
                            fontSize: "14px",
                            marginRight: "12px",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {item[newItem.xAxisKey || "name"] ||
                            `Item ${index + 1}`}
                        </span>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          <input
                            type="color"
                            value={currentColor}
                            onChange={(e) => {
                              const updatedData = newItem.data.map((d, i) =>
                                i === index
                                  ? {
                                      ...d,
                                      color: e.target.value,
                                    }
                                  : d
                              );
                              setNewItem({
                                ...newItem,
                                data: updatedData,
                              });
                            }}
                            style={{
                              width: "30px",
                              height: "30px",
                              padding: "2px",
                              borderRadius: "4px",
                              border: "1px solid #d1d5db",
                              cursor: "pointer",
                              marginRight: "8px",
                            }}
                          />
                          <span
                            style={{
                              fontSize: "12px",
                              color: "#6b7280",
                              minWidth: "40px",
                              textAlign: "center",
                            }}
                          >
                            {item[newItem.yAxisKeys[0] || "percentage_value"] ||
                              0}
                            %
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

          {newItem.chartType === "area" && (
            <>
              <div>
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: "#374151",
                  }}
                >
                  Area Opacity
                </label>
                <input
                  type="range"
                  min="10"
                  max="100"
                  value={newItem.areaOpacity || 50}
                  onChange={(e) =>
                    setNewItem({
                      ...newItem,
                      areaOpacity: parseInt(e.target.value),
                    })
                  }
                />
                <span style={{ marginLeft: "8px" }}>
                  {newItem.areaOpacity || 50}%
                </span>
              </div>
            </>
          )}
        </div>
        {/* Legend settings */}
        {newItem.chartType !== "radialProgress" &&
          newItem.chartType !== "ranker" && (
            <div style={{ flex: 1 }}>
              <div
                style={{
                  marginTop: newItem.chartType === "pie" ? "0rem" : "0",
                  display: newItem.chartType === "trend" ? "none" : "block",
                }}
              >
                <label
                  style={{
                    display: "block",
                    marginBottom: "8px",
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: "#374151",
                  }}
                >
                  Legend Position
                </label>
                <select
                  value={newItem.legendPosition || "bottom"}
                  onChange={(e) =>
                    setNewItem({
                      ...newItem,
                      legendPosition: e.target.value,
                    })
                  }
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    borderRadius: "8px",
                    border: "1px solid #d1d5db",
                    backgroundColor: "white",
                    fontSize: "14px",
                    color: "#374151",
                    outline: "none",
                  }}
                >
                  <option value="top">Top</option>
                  <option value="right">Right</option>
                  <option value="bottom">Bottom</option>
                  <option value="left">Left</option>
                  <option value="hidden">Hidden</option>
                </select>
              </div>
            </div>
          )}
      </div>

      {(newItem.chartType === "line" ||
        newItem.chartType === "area" ||
        newItem.chartType === "bar" ||
        newItem.chartType === "trend" ||
        newItem.chartType === "stackedBar" ||
        newItem.chartType === "composed") && (
        <div
          style={{
            display: "flex",
            gap: "8px",
            justifyContent: "space-between",
          }}
        >
          <div style={{ minWidth: "385px" }}>
            <label style={labelStyle}>X-Axis Label Orientation</label>
            <select
              value={newItem.xAxisOrientation || "horizontal"}
              onChange={(e) =>
                setNewItem({ ...newItem, xAxisOrientation: e.target.value })
              }
              style={dropdownStyle}
            >
              {xAxisOrientationOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {newItem.chartType === "pie" && (
        <>
          <div style={{ display: "flex", gap: "8px" }}>
            <div style={{ minWidth: "385px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "bold",
                  color: "#374151",
                }}
              >
                Inner Radius (Donut)
              </label>
              <input
                type="range"
                min="0"
                max="90"
                value={newItem.innerRadius || 0}
                onChange={(e) =>
                  setNewItem({
                    ...newItem,
                    innerRadius: parseInt(e.target.value),
                  })
                }
              />
              <span style={{ marginLeft: "8px" }}>
                {newItem.innerRadius || 0}%
              </span>
            </div>
            <div style={{ marginTop: "16px", flex: 1 }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "bold",
                  color: "#374151",
                  cursor: "pointer",
                }}
              >
                <input
                  type="checkbox"
                  checked={newItem.showLabels !== false} // Default to true if undefined
                  onChange={(e) =>
                    setNewItem({
                      ...newItem,
                      showLabels: e.target.checked,
                    })
                  }
                  style={{
                    width: "16px",
                    height: "16px",
                    accentColor: "#1a7a73",
                    cursor: "pointer",
                  }}
                />
                Show Labels
              </label>
            </div>
          </div>
        </>
      )}

      <div
        style={{
          display: "flex",
          gap: "8px",
        }}
      >
        <div style={{ minWidth: "385px" }}>
          <div>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 8,
                fontSize: 14,
                fontWeight: 500,
                color: "#374151",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={!!newItem.showDownloadExcelChart}
                onChange={(e) =>
                  setNewItem({
                    ...newItem,
                    showDownloadExcelChart: e.target.checked,
                  })
                }
                style={{
                  width: 16,
                  height: 16,
                  accentColor: "#1a7a73",
                  cursor: "pointer",
                }}
              />
              Include Download Excel Button
            </label>
          </div>
        </div>

        <div>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              marginBottom: 8,
              fontSize: 14,
              fontWeight: 500,
              color: "#374151",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={!!newItem.showDownloadPDFChart}
              onChange={(e) =>
                setNewItem({
                  ...newItem,
                  showDownloadPDFChart: e.target.checked,
                })
              }
              style={{
                width: 16,
                height: 16,
                accentColor: "#1a7a73",
                cursor: "pointer",
              }}
            />
            Include Download PDF Button
          </label>
        </div>
      </div>

      <div
        style={{
          display: "flex",
          gap: "8px",
          marginBottom: "16px",
        }}
      >
        <div style={{ minWidth: "385px" }}>
          <div>
            {/* Enable PDF Title Checkbox */}
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 8,
                fontSize: 14,
                fontWeight: 500,
                color: "#374151",
                cursor: "pointer",
              }}
            >
              <input
                type="checkbox"
                checked={newItem.enableChartPDFTitle || false}
                onChange={(e) => {
                  const isEnabled = e.target.checked;
                  setNewItem({
                    ...newItem,
                    enableChartPDFTitle: isEnabled,
                    // Clear the title when checkbox is unchecked
                    pdfChartTitle: isEnabled ? newItem.pdfChartTitle : "",
                  });
                }}
                style={{
                  width: 16,
                  height: 16,
                  accentColor: "#1a7a73",
                  cursor: "pointer",
                }}
              />
              Enable PDF/Excel Title
            </label>

            {/* PDF Title Input Field - Only show when enabled */}
            {newItem.enableChartPDFTitle && (
              <>
                <label
                  style={{
                    display: "block",
                    marginBottom: 8,
                    fontSize: 14,
                    fontWeight: 500,
                    color: "#374151",
                    marginLeft: 24,
                  }}
                >
                  PDF/Excel Title
                </label>
                <input
                  type="text"
                  value={newItem.pdfChartTitle || ""}
                  onChange={(e) =>
                    setNewItem({
                      ...newItem,
                      pdfChartTitle: e.target.value,
                    })
                  }
                  placeholder="Enter PDF/Excel title"
                  maxLength={100}
                  style={{
                    width: "90%",
                    padding: "8px 12px",
                    borderRadius: 6,
                    border: "1px solid #d1d5db",
                    fontSize: 14,
                    outline: "none",
                    transition: "border-color 0.2s",
                    marginLeft: 24,
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#1a7a73")}
                  onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
                />
                <div
                  style={{
                    fontSize: 12,
                    color: "#6b7280",
                    marginTop: 4,
                    marginLeft: 24,
                  }}
                >
                  Character count: {(newItem.pdfChartTitle || "").length}/100
                </div>
              </>
            )}
          </div>
        </div>
        {/* {userRole !== "SUPER ADMIN" && ( */}
        <div style={{ flex: 1 }}>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "8px",
              fontSize: "14px",
              fontWeight: "500",
              color: "#374151",
              cursor: "pointer",
            }}
          >
            <input
              type="checkbox"
              checked={newItem.showBankLogo || false}
              onChange={(e) =>
                setNewItem({
                  ...newItem,
                  showBankLogo: e.target.checked,
                })
              }
              style={{
                width: "16px",
                height: "16px",
                accentColor: "#1a7a73",
                cursor: "pointer",
              }}
            />
            Include Bank/Branch Logo in Downloads
          </label>
        </div>
        {/* )} */}
      </div>
    </>
  );
};

export default ChartEditPopUp;
